import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { IColumnTypes } from 'components/spreadsheet/types'
import { getRowHeightVariable, isSameValueStringValue } from 'components/spreadsheet/helpers/functions'
import {
  addThousandSeparator,
  convertToDuration,
  convertToFloat,
  formatPercentDisplayValue
} from 'components/spreadsheet/helpers/format'
import { cellStyles } from 'components/spreadsheet/components/cell/styles'

interface NumberCellProps {
  value: number | null
  color: string
  kind: IColumnTypes
  readOnly: boolean
  editing: boolean
  rowHeight: number
  selected: boolean
  validation: boolean
  handleKeyDown: (event: React.KeyboardEvent) => void
  setCellValue: (value: string | null) => void
  thousandSeparator: boolean
  decimalPlaces: number
}

const styles = cellStyles()

const NumberCellComponent: React.FC<NumberCellProps> = ({
  value,
  color,
  kind,
  readOnly,
  editing,
  rowHeight,
  selected,
  validation,
  handleKeyDown,
  setCellValue,
  thousandSeparator,
  decimalPlaces
}) => {
  const [currentValue, setCurrentValue] = useState<string>(
    value !== null && value !== undefined ? value.toString() : ''
  )

  const inputAreaRef = useRef<HTMLInputElement>(null)
  const heightPx = getRowHeightVariable(rowHeight)

  useLayoutEffect(() => {
    if (!editing) return

    if (inputAreaRef.current) {
      inputAreaRef.current.focus()
      inputAreaRef.current.select()
    }

    // Deferred re-focus for form view where the input needs a frame to settle into layout
    const frameId = requestAnimationFrame(() => {
      if (inputAreaRef.current) {
        inputAreaRef.current.focus()
      }
    })
    return () => cancelAnimationFrame(frameId)
  }, [editing])

  useEffect(() => {
    if (!isSameValueStringValue(currentValue, value)) {
      setCurrentValue(value !== null && value !== undefined ? value.toString() : '')
    }
  }, [value])

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(event.target.value)
    if (event.target.value === '' && kind !== 'duration') {
      setCurrentValue(event.target.value)
      setCellValue(event.target.value)
    } else if (kind === 'percentage' && !Number.isNaN(newValue)) {
      const percent = newValue / 100
      setCurrentValue(String(percent))
      setCellValue(String(percent))
    } else if (kind === 'integer' && Number.isInteger(newValue)) {
      setCurrentValue(event.target.value)
      setCellValue(String(newValue))
    } else if (kind !== 'integer' && kind !== 'duration' && event.target.value === '.') {
      setCurrentValue(event.target.value)
      setCellValue(event.target.value)
    } else if (kind !== 'integer' && kind !== 'duration' && !Number.isNaN(newValue)) {
      setCurrentValue(event.target.value)
      setCellValue(event.target.value)
    } else if (kind === 'duration') {
      let duration = 0
      let negative = false
      const inputs = event.target.value.split(':')
      const hours = inputs[0]
      if (hours.includes('-')) {
        negative = true
      }

      let numberHours = Number.parseInt(hours)
      numberHours = Math.abs(numberHours)

      const minutes = inputs[1]
      const numberMinutes = Number.parseInt(minutes)

      const seconds = inputs[2]
      const numberSeconds = Number.parseInt(seconds)

      if (!isNaN(numberHours)) duration += numberHours * 60 * 60
      if (!isNaN(numberMinutes)) duration += numberMinutes * 60
      if (!isNaN(numberSeconds)) duration += numberSeconds

      if (negative) duration *= -1

      setCurrentValue(String(duration))
      setCellValue(String(duration))
    }
  }

  if (editing && !readOnly) {
    return (
      <input
        ref={inputAreaRef}
        className={styles.editing({ focused: true, class: 'cell-value-editing' })}
        data-focussed
        type={kind === 'duration' ? 'text' : 'number'}
        defaultValue={kind === 'duration' ? convertToDuration(currentValue, thousandSeparator) : currentValue}
        style={{ height: `${heightPx - 1}px` }}
        onChange={handleOnChange}
        onKeyDown={handleKeyDown}
      />
    )
  }

  const formattedValue = (() => {
    const val = currentValue !== undefined ? currentValue : ''
    // A number column can briefly hold a non-numeric string — e.g. text ("hello") or whitespace
    // left in local state immediately after converting a text column to a number column, before the
    // row refetch clears it (#10272). It has no numeric representation, so render blank rather than
    // the raw text, "NaN", or "0%". Trim first because Number('   ') is 0 (not NaN), which would
    // otherwise slip through to the per-kind formatters below.
    const trimmed = val.trim()
    if (trimmed === '' || Number.isNaN(Number(trimmed))) return ''
    switch (kind) {
      case 'percentage': {
        const num = Number(currentValue)
        return currentValue === '' || Number.isNaN(num) ? '' : formatPercentDisplayValue(num, 'en-GB', decimalPlaces)
      }
      case 'duration':
        return convertToDuration(currentValue, thousandSeparator)
      case 'float':
        return convertToFloat(currentValue, thousandSeparator, decimalPlaces)
      default:
        return thousandSeparator ? addThousandSeparator(val) : val
    }
  })()

  return (
    <div
      className={styles.value({ focused: selected, class: 'cell-value fit-text text-right' })}
      data-focussed={selected || undefined}
      tabIndex={-1}
      style={{
        color: color,
        width: 'var(--cell-width)',
        maxWidth: 'var(--cell-width)',
        maxHeight: `${heightPx - 1}px`
      }}
    >
      <div
        className={styles.content({
          validation,
          class: 'break-words whitespace-pre-wrap overflow-hidden max-h-inherit'
        })}
      >
        {formattedValue}
      </div>
    </div>
  )
}

export const NumberCell = NumberCellComponent
